<template>
  <div class="relative max-w-full" ref="filterElementRef">
    <div
      class="group w-fit border flex rounded-lg font-medium tracking-wide transition select-none text-sm"
      :class="filterClasses.wrapper[filterState]"
    >
      <button
        :id="filterButtonId"
        @click.stop="!disabled && toggleDropdown()"
        class="flex-1 min-w-0 flex"
        :aria-haspopup="true"
        :aria-expanded="isOpen"
        :aria-controls="dropdownId"
        :aria-disabled="disabled"
      >
        <span
          class="pl-2.5 py-0.5 flex items-center"
          :class="{
            'pr-1': !active,
            'pr-2.5': active,
          }"
        >
          <span class="sr-only">
            {{
              t('aria-text.filter', {
                property: active ? activeLabel || label : label,
              })
            }}
          </span>
          <span aria-hidden="true">
            {{ active ? activeLabel || label : label }}
          </span>
        </span>
        <span
          v-if="active"
          class="flex-1 min-w-0 -my-px pl-2.5 pr-2 py-0.5 bg-blue-100/70 hover:bg-blue-100 border-blue-200/40 text-blue-700 shadow-btn-blue-light cursor-pointer border rounded-l-lg flex items-center"
        >
          <span class="truncate">
            <slot name="value">
              {{ value }}
            </slot>
          </span>
        </span>
        <span
          v-if="!active"
          class="pr-2 py-0.5 grid place-items-center opacity-70 group-hover:opacity-100"
        >
          <s-icon
            name="filter"
            class="transition col-start-1 row-start-1"
            size="14"
            :class="[isOpen ? 'opacity-0 rotate-45' : 'opacity-100']"
            aria-hidden="true"
          />
          <s-icon
            name="close"
            class="transition col-start-1 row-start-1"
            size="16"
            :class="[isOpen ? 'opacity-100' : 'opacity-0 -rotate-45']"
            aria-hidden="true"
          />
        </span>
      </button>
      <button
        v-if="active && !disabled"
        @click.stop="$emit('clear')"
        class="-my-px -mr-px pl-1.5 pr-2 py-0.5 bg-blue-100/70 hover:bg-blue-100 border-blue-200/40 text-blue-700 shadow-btn-blue-light cursor-pointer border border-l-0 rounded-r-lg"
        :class="{
          'border-blue-200/50': !isOpen,
          'border-gray-200': isOpen,
        }"
        :aria-label="t('actions.clearFilter', {property: activeLabel || label})"
      >
        <s-icon name="close" size="16" aria-hidden="true" />
      </button>
    </div>

    <SDialog v-model:open="isOpen" :modal="false" class="p-0">
      <div
        ref="dropdownRef"
        :id="dropdownId"
        :style="floatingStyles"
        @mousedown.stop
        class="z-50 rounded-[calc(0.5rem-2px)] shadow-card bg-white"
        padding="none"
        role="menu"
        :aria-label="active ? value : label"
      >
        <slot @close="isOpen = false" v-bind="{close}"></slot>
      </div>
    </SDialog>
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue';
import {useFloating, autoUpdate, offset, shift, flip} from '@floating-ui/vue';
import SIcon from './SIcon.vue';
import SDialog from '../components/SDialog.vue';
import {useI18n} from 'vue-i18n';
import {useId} from 'vue';
import {useEventListener} from '@vueuse/core';

const props = defineProps<{
  label: string;
  activeLabel?: string;
  value?: string;
  disabled?: boolean;
}>();

const active = defineModel<boolean>('active');
const emit = defineEmits(['clear']);

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const filterButtonId = useId();

// Dropdown state
const isOpen = ref(false);
const close = () => (isOpen.value = false);
const dropdownId = useId();

const filterClasses = {
  wrapper: {
    disabled: 'opacity-50 cursor-not-allowed pointer-events-none',
    open: 'bg-white border-gray-200 text-blue-700 shadow-btn cursor-pointer',
    active:
      'bg-gray-100 border-gray-200 text-gray-600 hover:bg-white hover:text-blue-700 hover:border-blue-200/40 cursor-pointer shadow-inset',
    inactive:
      'bg-gray-100 border-gray-200 text-gray-600 hover:bg-white hover:text-blue-700 hover:border-blue-200/40 cursor-pointer shadow-inset',
  },
  disabled: 'opacity-50 cursor-not-allowed pointer-events-none',
  open: 'bg-white border-gray-200 text-blue-700 shadow-btn cursor-pointer',
  active:
    'bg-blue-100/70 hover:bg-blue-100 border-blue-200/40 text-blue-700 shadow-btn cursor-pointer',
  inactive:
    'bg-gray-100 border-gray-200 text-gray-600 hover:bg-white hover:text-blue-700 hover:border-blue-200/40 cursor-pointer shadow-inset',
};

const filterState = computed(() => {
  if (props.disabled) {
    return 'disabled';
  }

  if (isOpen.value) {
    return 'open';
  }

  if (active.value) {
    return 'active';
  }

  return 'inactive';
});

// Floating UI refs and setup
const filterElementRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const middleware = ref([offset(8), shift(), flip()]);

// Setup floating UI
const {floatingStyles} = useFloating(filterElementRef, dropdownRef, {
  placement: 'bottom-start',
  middleware,
  whileElementsMounted: autoUpdate,
});

function toggleDropdown(event?: Event): void {
  if (event) {
    event.stopPropagation();
  }

  if (props.disabled) return;

  isOpen.value = !isOpen.value;
}

// Handle outside clicks (ESC and focus trapping are now handled by SDialog)
function handleClickOutside(event: MouseEvent) {
  if (
    isOpen.value &&
    filterElementRef.value &&
    !filterElementRef.value.contains(event.target as Node) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
}

// Add event listeners for outside clicks
useEventListener(document, 'mousedown', handleClickOutside, true);
</script>

<style scoped>
.shadow-inset {
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.shadow-inset-blue {
  box-shadow: inset 0 1px 2px rgba(59, 130, 246, 0.05);
}

.card-soft {
  background-color: white;
  border-radius: 0.375rem;
}
</style>

<i18n>
{
  "en": {
    "actions.clearFilter": "Clear {property} filter",
    "aria-text.filter": "Filter by {property}:"
  },
  "fr": {
    "actions.clearFilter": "Effacer le filtre {property}",
    "aria-text.filter": "Filtrer par {property} :"
  }
}
</i18n>
